import type {
  NovaRunResultItem,
  NovaRunResultPresentation,
  NovaRunResultSection,
  NovaRunResultStat,
} from '@tabbio/types';
import type { NovaRunDetail } from '../../lib/automation-api';

const PRESENTATION_LIMITS = {
  title: 160,
  body: 2_000,
  stats: 6,
  sections: 8,
  itemsPerSection: 20,
} as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function readText(value: unknown, maxLength: number): string | null {
  if (typeof value !== 'string') return null;
  const result = value.trim();
  return result ? result.slice(0, maxLength) : null;
}

function readResultStat(
  value: unknown,
  index: number,
): NovaRunResultStat | null {
  if (!isRecord(value)) return null;
  const label = readText(value.label, PRESENTATION_LIMITS.title);
  const rawValue = value.value;
  if (
    !label ||
    (typeof rawValue !== 'string' && typeof rawValue !== 'number') ||
    (typeof rawValue === 'number' && !Number.isFinite(rawValue))
  ) {
    return null;
  }
  return {
    id: readText(value.id, 120) ?? `stat-${index}`,
    label,
    value:
      typeof rawValue === 'string'
        ? rawValue.trim().slice(0, PRESENTATION_LIMITS.title)
        : rawValue,
  };
}

function readResultItem(
  value: unknown,
  index: number,
): NovaRunResultItem | null {
  if (!isRecord(value)) return null;
  const title = readText(value.title, PRESENTATION_LIMITS.title);
  if (!title) return null;
  return {
    id: readText(value.id, 120) ?? `item-${index}`,
    title,
    subtitle: readText(value.subtitle, PRESENTATION_LIMITS.title),
    body: readText(value.body, PRESENTATION_LIMITS.body),
    badge: readText(value.badge, 80),
  };
}

function readResultSection(
  value: unknown,
  index: number,
): NovaRunResultSection | null {
  if (!isRecord(value)) return null;
  const title = readText(value.title, PRESENTATION_LIMITS.title);
  const body = readText(value.body, PRESENTATION_LIMITS.body);
  const items = Array.isArray(value.items)
    ? value.items
        .slice(0, PRESENTATION_LIMITS.itemsPerSection)
        .map(readResultItem)
        .filter((item): item is NovaRunResultItem => Boolean(item))
    : [];
  if (!title && !body && !items.length) return null;
  return {
    id: readText(value.id, 120) ?? `section-${index}`,
    title,
    body,
    items,
  };
}

/**
 * Reads the explicit, display-safe result contract emitted by automation
 * steps. Raw provider/step JSON is never rendered as a fallback.
 */
export function extractRunPresentation(
  detail: NovaRunDetail | null,
): NovaRunResultPresentation | null {
  if (!detail?.steps?.length) return null;
  const documents = detail.steps
    .map((step) => step.output?.presentation)
    .filter(isRecord);
  if (!documents.length) return null;

  const stats = documents
    .flatMap((document) =>
      Array.isArray(document.stats) ? document.stats : [],
    )
    .slice(0, PRESENTATION_LIMITS.stats)
    .map(readResultStat)
    .filter((stat): stat is NovaRunResultStat => Boolean(stat));
  const sections = documents
    .flatMap((document) =>
      Array.isArray(document.sections) ? document.sections : [],
    )
    .slice(0, PRESENTATION_LIMITS.sections)
    .map(readResultSection)
    .filter((section): section is NovaRunResultSection => Boolean(section));
  const title = documents
    .map((document) => readText(document.title, PRESENTATION_LIMITS.title))
    .find(Boolean);
  const summary = documents
    .map((document) => readText(document.summary, PRESENTATION_LIMITS.body))
    .find(Boolean);

  if (!title && !summary && !stats.length && !sections.length) return null;
  return { title, summary, stats, sections };
}

/**
 * A research run whose report had nothing new since the previous run: it
 * delivered quietly (no email, no notification, no files), and the panel
 * says so instead of presenting a result.
 */
export function isQuietReportRun(detail: NovaRunDetail | null): boolean {
  if (!detail?.steps?.length) return false;
  return detail.steps.some((step) => {
    const report = step.output?.report;
    return (
      step.type === 'nova.deliver' &&
      isRecord(report) &&
      report.quiet === true
    );
  });
}

